import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tabs,
  Typography,
  message
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const { TextArea } = Input;
const { Title, Text } = Typography;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/** 精靈各步未掛載時欄位仍保留在 store；validateFields() 回傳值不含這些欄位，送 API 前須 getFieldsValue(true)。 */
const WIZARD_ALL_FIELD_NAMES = [
  'isVirtual',
  'industry',
  'department',
  'jobTitle',
  'avatarUrl',
  'gender',
  'displayName',
  'birthDate',
  'birthplace',
  'background',
  'workExperience',
  'professionalSkills',
  'personality',
  'jobDescription',
  'responsibilities',
  'memoryMd'
];

/** /Uploads/* 由後端授權；img/Avatar 的 src 不會帶 Bearer，需用 fetch + blob 才能預覽。 */
const needsAuthUploadFetch = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('/Uploads/')) return true;
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.startsWith('/Uploads/') && u.origin === window.location.origin;
  } catch {
    return false;
  }
};

const useAuthUploadSrc = (rawUrl) => {
  const [blobSrc, setBlobSrc] = useState(null);
  const blobRef = useRef(null);

  useEffect(() => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
    setBlobSrc(null);
    if (!rawUrl || !needsAuthUploadFetch(rawUrl)) return;

    let cancelled = false;
    const token = localStorage.getItem('token');
    fetch(rawUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include'
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        blobRef.current = u;
        setBlobSrc(u);
      })
      .catch(() => {
        if (!cancelled) setBlobSrc(null);
      });

    return () => {
      cancelled = true;
    };
  }, [rawUrl]);

  useEffect(
    () => () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    },
    []
  );

  return useMemo(() => {
    if (!rawUrl) return undefined;
    if (needsAuthUploadFetch(rawUrl)) return blobSrc || undefined;
    return rawUrl;
  }, [rawUrl, blobSrc]);
};

const AvatarFromUploadUrl = ({ url, size, children }) => {
  const src = useAuthUploadSrc(url);
  return (
    <Avatar size={size} src={src}>
      {children}
    </Avatar>
  );
};

const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const d = dayjs(birthDate);
  if (!d.isValid()) return null;
  return dayjs().diff(d, 'year');
};

/** 新增／編輯抽屜寬度；頭像預覽像素 */
const DRAWER_WIDTH = '90%';
const AVATAR_PREVIEW_SIZE = 120;
const LIST_TILE_AVATAR_SIZE = 112;
const LIST_VIEW_STORAGE_KEY = 'aiEmployeeListView';

function AiEmployeeProfilesPage() {
  const { t, tLookup, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const [lookups, setLookups] = useState({ birthplaces: [], departments: [], jobTitles: [], industries: [] });
  const departmentOptions = useMemo(
    () => lookups.departments.map((v) => ({ value: v, label: tLookup('departments', v) })),
    [lookups.departments, tLookup, currentLanguage]
  );
  const jobTitleOptions = useMemo(
    () => lookups.jobTitles.map((v) => ({ value: v, label: tLookup('jobTitles', v) })),
    [lookups.jobTitles, tLookup, currentLanguage]
  );
  const birthplaceOptions = useMemo(
    () => lookups.birthplaces.map((v) => ({ value: v, label: tLookup('birthplaces', v) })),
    [lookups.birthplaces, tLookup, currentLanguage]
  );
  const industryOptions = useMemo(
    () => lookups.industries.map((v) => ({ value: v, label: tLookup('industries', v) })),
    [lookups.industries, tLookup, currentLanguage]
  );
  const [aiProviders, setAiProviders] = useState([]);
  const [avatarProviderKey, setAvatarProviderKey] = useState(undefined);
  const [expandProviderKey, setExpandProviderKey] = useState(undefined);

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiFieldKey, setAiFieldKey] = useState('');
  const [aiHint, setAiHint] = useState('');
  const [aiSubmitting, setAiSubmitting] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [listViewMode, setListViewMode] = useState(() => {
    try {
      return localStorage.getItem(LIST_VIEW_STORAGE_KEY) === 'tile' ? 'tile' : 'table';
    } catch {
      return 'table';
    }
  });

  const setListViewModePersist = useCallback((mode) => {
    setListViewMode(mode);
    try {
      localStorage.setItem(LIST_VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetch('/ai-employee-lookups.json')
      .then((r) => r.json())
      .then((data) => {
        setLookups({
          birthplaces: data.birthplaces || [],
          departments: data.departments || [],
          jobTitles: data.jobTitles || [],
          industries: data.industries || []
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/apiproviders/company', { headers: authHeaders() });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const list = Array.isArray(data)
          ? data.filter((p) => (p.category || '').toUpperCase() === 'AI' && p.active)
          : [];
        if (cancelled) return;
        setAiProviders(list);
        if (list.length) {
          const preferred = list.find((p) => p.providerKey === 'xai') || list[0];
          setAvatarProviderKey((prev) => prev ?? preferred.providerKey);
          setExpandProviderKey((prev) => prev ?? preferred.providerKey);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        ...(search.trim() ? { search: search.trim() } : {})
      });
      const res = await fetch(`/api/aivirtualemployees?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.data || []);
      setTotal(json.total ?? 0);
    } catch (e) {
      message.error(e.message || t('aiEmployeeProfiles.loadFailed'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const buildContextPayload = () => {
    // true：精靈切步驟時仍帶入已填的部門／職稱／生日等，供 AI 依人設生成
    const v = form.getFieldsValue(true);
    return {
      industry: v.industry,
      department: v.department,
      jobTitle: v.jobTitle,
      gender: v.gender,
      displayName: v.displayName,
      birthDate: v.birthDate ? v.birthDate.toDate?.() || v.birthDate : null,
      birthplace: v.birthplace,
      background: v.background,
      workExperience: v.workExperience,
      professionalSkills: v.professionalSkills,
      personality: v.personality,
      jobDescription: v.jobDescription,
      responsibilities: Array.isArray(v.responsibilities) ? v.responsibilities : []
    };
  };

  const openAiExpand = (fieldKey) => {
    setAiFieldKey(fieldKey);
    setAiHint('');
    setAiModalOpen(true);
  };

  /** 呼叫擴寫 API 並寫入表單（姓名欄可無 userHint，其餘欄位由 Modal 輸入提示）。 */
  const performExpandField = async (fieldKey, userHint) => {
    const res = await fetch('/api/aivirtualemployees/ai/expand-field', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        fieldKey,
        userHint: userHint || undefined,
        providerKey: expandProviderKey || undefined,
        context: buildContextPayload(),
        uiLanguage: currentLanguage,
        employeeId: editingId || undefined
      })
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || 'AI failed');
    const content = json.content;
    if (json.memoryMd != null) {
      form.setFieldValue('memoryMd', json.memoryMd);
    }
    if (content != null) {
      if (fieldKey === 'responsibilities') {
        const parts = content
          .split(/[\n,，、]/)
          .map((s) => s.trim())
          .filter(Boolean);
        form.setFieldValue('responsibilities', parts.length ? parts : [content.trim()]);
      } else {
        form.setFieldValue(fieldKey, content);
      }
      message.success(t('aiEmployeeProfiles.aiApplied'));
    }
  };

  /** 姓名：不開 Modal，依表單脈絡直接生成。 */
  const runDisplayNameExpandDirect = async () => {
    setAiFieldKey('displayName');
    setAiSubmitting(true);
    try {
      await performExpandField('displayName', undefined);
    } catch (e) {
      message.error(e.message || 'AI failed');
    } finally {
      setAiSubmitting(false);
    }
  };

  const runAiExpand = async () => {
    if (!aiFieldKey) return;
    setAiSubmitting(true);
    try {
      await performExpandField(aiFieldKey, aiHint || undefined);
      setAiModalOpen(false);
    } catch (e) {
      message.error(e.message || 'AI failed');
    } finally {
      setAiSubmitting(false);
    }
  };

  const runGenerateAvatar = async () => {
    const v = form.getFieldsValue(true);
    if (!avatarProviderKey) {
      message.warning(t('aiEmployeeProfiles.selectAiProvider'));
      return;
    }
    setAvatarLoading(true);
    try {
      const res = await fetch('/api/aivirtualemployees/ai/generate-avatar', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          providerKey: avatarProviderKey,
          gender: v.gender,
          birthDate: v.birthDate ? dayjs(v.birthDate).format('YYYY-MM-DD') : null,
          birthplace: v.birthplace,
          displayName: v.displayName,
          background: v.background,
          workExperience: v.workExperience,
          professionalSkills: v.professionalSkills,
          personality: v.personality,
          industry: v.industry,
          department: v.department,
          jobTitle: v.jobTitle,
          employeeId: editingId || undefined
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || json.code || 'Avatar failed');
      if (json.memoryMd != null) {
        form.setFieldValue('memoryMd', json.memoryMd);
      }
      if (json.avatarUrl) {
        form.setFieldValue('avatarUrl', json.avatarUrl);
        message.success(t('aiEmployeeProfiles.avatarOk'));
      }
    } catch (e) {
      message.error(e.message || 'Avatar failed');
    } finally {
      setAvatarLoading(false);
    }
  };

  const labelWithAi = (fieldKey, labelText) => (
    <Space size="small">
      <span>{labelText}</span>
      <Button
        type="link"
        size="small"
        icon={<ThunderboltOutlined />}
        loading={aiSubmitting && aiFieldKey === fieldKey}
        onClick={() => {
          if (fieldKey === 'displayName') {
            void runDisplayNameExpandDirect();
          } else {
            openAiExpand(fieldKey);
          }
        }}
      />
    </Space>
  );

  /** AI 擴寫 Modal 內顯示欄位名稱（與表單標籤同一套 i18n，勿顯示 raw fieldKey）。 */
  const getAiExpandFieldLabel = (fieldKey) => {
    const map = {
      displayName: 'displayName',
      jobDescription: 'jobDescription',
      responsibilities: 'responsibilities',
      background: 'background',
      workExperience: 'workExperience',
      professionalSkills: 'professionalSkills',
      personality: 'personality'
    };
    const sub = map[fieldKey];
    return sub ? t(`aiEmployeeProfiles.${sub}`) : fieldKey;
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setWizardMode(false);
    setWizardStep(0);
    setEditingId(null);
    form.resetFields();
  };

  const openEdit = async (record) => {
    setWizardMode(false);
    setEditingId(record.id);
    try {
      const res = await fetch(`/api/aivirtualemployees/${record.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(String(res.status));
      const full = await res.json();
      form.setFieldsValue({
        ...full,
        birthDate: full.birthDate ? dayjs(full.birthDate) : null,
        responsibilities: Array.isArray(full.responsibilities) ? full.responsibilities : []
      });
    } catch {
      form.setFieldsValue({
        ...record,
        birthDate: record.birthDate ? dayjs(record.birthDate) : null,
        responsibilities: Array.isArray(record.responsibilities) ? record.responsibilities : [],
        memoryMd: record.memoryMd ?? ''
      });
      message.warning(t('aiEmployeeProfiles.loadDetailPartial'));
    }
    setDrawerOpen(true);
  };

  const openWizardCreate = () => {
    setWizardMode(true);
    setEditingId(null);
    setWizardStep(0);
    form.resetFields();
    form.setFieldsValue({ isVirtual: true, responsibilities: [] });
    setDrawerOpen(true);
  };

  const toPayload = (values) => ({
    isVirtual: values.isVirtual !== false,
    gender: values.gender || null,
    displayName:
      typeof values.displayName === 'string' ? values.displayName.trim() : values.displayName ?? '',
    birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : null,
    birthplace: values.birthplace || null,
    industry: values.industry || null,
    background: values.background || null,
    workExperience: values.workExperience || null,
    professionalSkills: values.professionalSkills || null,
    personality: values.personality || null,
    department: values.department || null,
    jobTitle: values.jobTitle || null,
    jobDescription: values.jobDescription || null,
    responsibilities: values.responsibilities || [],
    avatarUrl: values.avatarUrl || null,
    memoryMd: typeof values.memoryMd === 'string' ? values.memoryMd : values.memoryMd ?? null
  });

  const submit = async () => {
    try {
      if (wizardMode) {
        await form.validateFields(WIZARD_ALL_FIELD_NAMES);
      } else {
        await form.validateFields();
      }
      const values = form.getFieldsValue(true);
      const body = toPayload(values);
      const isEdit = !!editingId;
      const url = isEdit ? `/api/aivirtualemployees/${editingId}` : '/api/aivirtualemployees';
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      message.success(isEdit ? t('aiEmployeeProfiles.saved') : t('aiEmployeeProfiles.created'));
      closeDrawer();
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Save failed');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t('aiEmployeeProfiles.confirmDelete'),
      onOk: async () => {
        const res = await fetch(`/api/aivirtualemployees/${record.id}`, {
          method: 'DELETE',
          headers: authHeaders()
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          message.error(j.error || 'Delete failed');
          return;
        }
        message.success(t('aiEmployeeProfiles.deleted'));
        fetchList();
      }
    });
  };

  const birth = Form.useWatch('birthDate', form);
  const age = useMemo(() => calcAge(birth), [birth]);
  const watchedAvatarUrl = Form.useWatch('avatarUrl', form);
  const watchedDisplayName = Form.useWatch('displayName', form);

  const providerOptions = useMemo(
    () =>
      aiProviders.map((p) => ({
        value: p.providerKey,
        label: p.displayName || p.providerKey
      })),
    [aiProviders]
  );

  const tabWork = (
    <div>
      {editingId ? (
        <Form.Item name="employeeNumber" label={t('aiEmployeeProfiles.employeeNumber')}>
          <Input disabled />
        </Form.Item>
      ) : (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {t('aiEmployeeProfiles.employeeNumberAuto')}
        </Text>
      )}
      <Form.Item label={t('aiEmployeeProfiles.avatar')}>
        <div style={{ width: '100%' }}>
          <Form.Item name="avatarUrl" noStyle>
            <Input type="hidden" />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              marginBottom: 8
            }}
          >
            <AvatarFromUploadUrl url={watchedAvatarUrl} size={AVATAR_PREVIEW_SIZE}>
              {watchedDisplayName?.[0]}
            </AvatarFromUploadUrl>
            <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Select
                allowClear
                placeholder={t('aiEmployeeProfiles.avatarProvider')}
                style={{ width: '100%' }}
                options={providerOptions}
                value={avatarProviderKey}
                onChange={setAvatarProviderKey}
              />
              <Button icon={<ReloadOutlined />} loading={avatarLoading} onClick={runGenerateAvatar} block type="default">
                {t('aiEmployeeProfiles.randomAvatar')}
              </Button>
            </div>
          </div>
        </div>
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Form.Item name="gender" label={t('aiEmployeeProfiles.gender')}>
            <Select
              allowClear
              style={{ width: '100%' }}
              options={[
                { value: '男', label: t('aiEmployeeProfiles.genderMale') },
                { value: '女', label: t('aiEmployeeProfiles.genderFemale') },
                { value: '其他', label: t('aiEmployeeProfiles.genderOther') }
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            name="displayName"
            label={labelWithAi('displayName', t('aiEmployeeProfiles.displayName'))}
            rules={[{ required: true, message: t('aiEmployeeProfiles.nameRequired') }]}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="industry" label={t('aiEmployeeProfiles.industry')}>
            <Select allowClear showSearch style={{ width: '100%' }} options={industryOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="department" label={t('aiEmployeeProfiles.department')}>
            <Select allowClear showSearch style={{ width: '100%' }} options={departmentOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="jobTitle" label={t('aiEmployeeProfiles.jobTitle')}>
            <Select allowClear showSearch style={{ width: '100%' }} options={jobTitleOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="birthDate" label={t('aiEmployeeProfiles.birthDate')}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="birthplace" label={t('aiEmployeeProfiles.birthplace')}>
            <Select allowClear showSearch style={{ width: '100%' }} options={birthplaceOptions} />
          </Form.Item>
        </Col>
      </Row>
      {age != null && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {t('aiEmployeeProfiles.age')}: {age}
        </Text>
      )}
      <Form.Item name="jobDescription" label={labelWithAi('jobDescription', t('aiEmployeeProfiles.jobDescription'))}>
        <TextArea rows={4} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="responsibilities" label={labelWithAi('responsibilities', t('aiEmployeeProfiles.responsibilities'))}>
        <Select mode="tags" placeholder={t('aiEmployeeProfiles.responsibilitiesHint')} tokenSeparators={[',']} style={{ width: '100%' }} />
      </Form.Item>
    </div>
  );

  const tabPersonal = (
    <div>
      <Form.Item name="background" label={labelWithAi('background', t('aiEmployeeProfiles.background'))}>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="workExperience" label={labelWithAi('workExperience', t('aiEmployeeProfiles.workExperience'))}>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="professionalSkills" label={labelWithAi('professionalSkills', t('aiEmployeeProfiles.professionalSkills'))}>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="personality" label={labelWithAi('personality', t('aiEmployeeProfiles.personality'))}>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
    </div>
  );

  const tabMemory = (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, whiteSpace: 'pre-wrap' }}>
        {t('aiEmployeeProfiles.memoryHelp')}
      </Text>
      <Form.Item name="memoryMd" label={t('aiEmployeeProfiles.memoryLabel')}>
        <TextArea
          placeholder={t('aiEmployeeProfiles.memoryPlaceholder')}
          autoSize={{ minRows: 14, maxRows: 48 }}
          style={{ width: '100%', fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 13 }}
        />
      </Form.Item>
    </div>
  );

  const wizardStep0 = (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Form.Item name="industry" label={t('aiEmployeeProfiles.industry')} preserve>
            <Select allowClear showSearch style={{ width: '100%' }} options={industryOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="department" label={t('aiEmployeeProfiles.department')} rules={[{ required: true }]} preserve>
            <Select showSearch style={{ width: '100%' }} options={departmentOptions} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="jobTitle" label={t('aiEmployeeProfiles.jobTitle')} rules={[{ required: true }]} preserve>
        <Select showSearch style={{ width: '100%' }} options={jobTitleOptions} />
      </Form.Item>
    </>
  );

  const wizardStep1 = (
    <>
      <Form.Item name="avatarUrl" hidden preserve>
        <Input type="hidden" />
      </Form.Item>
      <Select
        style={{ marginBottom: 16, width: '100%', maxWidth: 480, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        placeholder={t('aiEmployeeProfiles.avatarProvider')}
        options={providerOptions}
        value={avatarProviderKey}
        onChange={setAvatarProviderKey}
      />
      <Form.Item label={t('aiEmployeeProfiles.avatar')} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
          <AvatarFromUploadUrl url={watchedAvatarUrl} size={AVATAR_PREVIEW_SIZE}>
            {watchedDisplayName?.[0]}
          </AvatarFromUploadUrl>
          <div style={{ width: '100%', maxWidth: 480 }}>
            <Button onClick={runGenerateAvatar} loading={avatarLoading} icon={<ReloadOutlined />} block type="default">
              {t('aiEmployeeProfiles.randomAvatar')}
            </Button>
          </div>
        </div>
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Form.Item name="gender" label={t('aiEmployeeProfiles.gender')} preserve>
            <Select
              style={{ width: '100%' }}
              options={[
                { value: '男', label: t('aiEmployeeProfiles.genderMale') },
                { value: '女', label: t('aiEmployeeProfiles.genderFemale') },
                { value: '其他', label: t('aiEmployeeProfiles.genderOther') }
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            name="displayName"
            label={labelWithAi('displayName', t('aiEmployeeProfiles.displayName'))}
            rules={[{ required: true, message: t('aiEmployeeProfiles.nameRequired') }]}
            preserve
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="birthDate" label={t('aiEmployeeProfiles.birthDate')} preserve>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="birthplace" label={t('aiEmployeeProfiles.birthplace')} preserve>
            <Select showSearch style={{ width: '100%' }} options={birthplaceOptions} />
          </Form.Item>
        </Col>
      </Row>
      {age != null && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {t('aiEmployeeProfiles.age')}: {age}
        </Text>
      )}
    </>
  );

  const wizardStep2 = (
    <>
      <Form.Item name="background" label={labelWithAi('background', t('aiEmployeeProfiles.background'))} preserve>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="workExperience" label={labelWithAi('workExperience', t('aiEmployeeProfiles.workExperience'))} preserve>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="professionalSkills" label={labelWithAi('professionalSkills', t('aiEmployeeProfiles.professionalSkills'))} preserve>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="personality" label={labelWithAi('personality', t('aiEmployeeProfiles.personality'))} preserve>
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
    </>
  );

  const wizardStep3 = (
    <>
      <Form.Item name="jobDescription" label={labelWithAi('jobDescription', t('aiEmployeeProfiles.jobDescription'))} preserve>
        <TextArea rows={4} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="responsibilities" label={labelWithAi('responsibilities', t('aiEmployeeProfiles.responsibilities'))} preserve>
        <Select mode="tags" tokenSeparators={[',']} style={{ width: '100%' }} />
      </Form.Item>
      <Text type="secondary">{t('aiEmployeeProfiles.wizardReviewHint')}</Text>
      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        {t('aiEmployeeProfiles.memoryWizardHint')}
      </Text>
    </>
  );

  const wizardSteps = [
    { title: t('aiEmployeeProfiles.wizardStepOrg'), content: wizardStep0 },
    { title: t('aiEmployeeProfiles.wizardStepProfile'), content: wizardStep1 },
    { title: t('aiEmployeeProfiles.wizardStepBio'), content: wizardStep2 },
    { title: t('aiEmployeeProfiles.wizardStepRole'), content: wizardStep3 }
  ];

  const nextWizard = async () => {
    const fields =
      wizardStep === 0
        ? ['department', 'jobTitle']
        : wizardStep === 1
          ? ['displayName']
          : [];
    try {
      if (fields.length) await form.validateFields(fields);
      setWizardStep((s) => Math.min(s + 1, wizardSteps.length - 1));
    } catch {
      /* validation */
    }
  };

  const columns = [
    {
      title: t('aiEmployeeProfiles.employeeNumber'),
      dataIndex: 'employeeNumber',
      width: 140
    },
    {
      title: t('aiEmployeeProfiles.avatar'),
      dataIndex: 'avatarUrl',
      width: 72,
      render: (url, record) => (
        <AvatarFromUploadUrl url={url} size={40}>
          {record.displayName?.[0]}
        </AvatarFromUploadUrl>
      )
    },
    { title: t('aiEmployeeProfiles.displayName'), dataIndex: 'displayName' },
    {
      title: t('aiEmployeeProfiles.department'),
      dataIndex: 'department',
      render: (v) => (v ? tLookup('departments', v) : '')
    },
    {
      title: t('aiEmployeeProfiles.jobTitle'),
      dataIndex: 'jobTitle',
      render: (v) => (v ? tLookup('jobTitles', v) : '')
    },
    {
      title: t('aiEmployeeProfiles.createdAt'),
      dataIndex: 'createdAt',
      width: 180,
      render: (d) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '')
    },
    {
      title: t('aiEmployeeProfiles.actions'),
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space wrap>
              <Segmented
                value={listViewMode}
                onChange={setListViewModePersist}
                options={[
                  { value: 'table', icon: <UnorderedListOutlined />, label: t('aiEmployeeProfiles.viewList') },
                  { value: 'tile', icon: <AppstoreOutlined />, label: t('aiEmployeeProfiles.viewTile') }
                ]}
              />
              <Input.Search
                allowClear
                placeholder={t('aiEmployeeProfiles.searchPlaceholder')}
                onSearch={(v) => {
                  setPage(1);
                  setSearch(v);
                }}
                style={{ width: 260 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openWizardCreate}>
                {t('aiEmployeeProfiles.create')}
              </Button>
            </Space>
            <Title level={3} style={{ margin: 0, textAlign: 'right' }}>
              {t('aiEmployeeProfiles.title')}
            </Title>
          </Space>

          {listViewMode === 'table' ? (
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }
              }}
            />
          ) : (
            <Spin spinning={loading}>
              {rows.length === 0 && !loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <>
                  <Row gutter={[16, 16]}>
                    {rows.map((record) => (
                      <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={3} key={record.id}>
                        <Card
                          hoverable
                          size="small"
                          styles={{ body: { padding: 16 } }}
                          style={{ height: '100%', textAlign: 'center' }}
                        >
                          <div style={{ marginBottom: 12 }}>
                            <AvatarFromUploadUrl url={record.avatarUrl} size={LIST_TILE_AVATAR_SIZE}>
                              {record.displayName?.[0]}
                            </AvatarFromUploadUrl>
                          </div>
                          <Text strong style={{ display: 'block', fontSize: 15 }} ellipsis={{ tooltip: record.displayName }}>
                            {record.displayName || '—'}
                          </Text>
                          {(record.department || record.jobTitle) && (
                            <Text
                              type="secondary"
                              style={{ display: 'block', fontSize: 12, marginTop: 6, lineHeight: 1.4 }}
                              ellipsis={{ tooltip: true }}
                            >
                              {[
                                record.department ? tLookup('departments', record.department) : null,
                                record.jobTitle ? tLookup('jobTitles', record.jobTitle) : null
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Text>
                          )}
                          <Space size="small" style={{ marginTop: 12, justifyContent: 'center' }}>
                            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                            <Button
                              type="link"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDelete(record)}
                            />
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <Pagination
                    style={{ marginTop: 20, justifyContent: 'flex-end', display: 'flex' }}
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    onChange={(p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    }}
                  />
                </>
              )}
            </Spin>
          )}
        </Space>
      </Card>

      <Drawer
        title={editingId ? t('aiEmployeeProfiles.editTitle') : t('aiEmployeeProfiles.createTitle')}
        width={DRAWER_WIDTH}
        styles={{ body: { padding: '16px 24px 24px' } }}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
        extra={
          <Space>
            <Select
              allowClear
              style={{ minWidth: 160 }}
              placeholder={t('aiEmployeeProfiles.expandProvider')}
              options={providerOptions}
              value={expandProviderKey}
              onChange={setExpandProviderKey}
            />
          </Space>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ isVirtual: true, responsibilities: [] }}>
          <Form.Item name="isVirtual" hidden>
            <Input />
          </Form.Item>

          {wizardMode ? (
            <>
              <Steps current={wizardStep} items={wizardSteps.map((s) => ({ title: s.title }))} style={{ marginBottom: 24 }} />
              {wizardSteps[wizardStep].content}
              <Space style={{ marginTop: 24 }}>
                <Button disabled={wizardStep === 0} onClick={() => setWizardStep((s) => s - 1)}>
                  {t('aiEmployeeProfiles.prev')}
                </Button>
                {wizardStep < wizardSteps.length - 1 ? (
                  <Button type="primary" onClick={nextWizard}>
                    {t('aiEmployeeProfiles.next')}
                  </Button>
                ) : (
                  <Button type="primary" onClick={submit}>
                    {t('aiEmployeeProfiles.submitCreate')}
                  </Button>
                )}
              </Space>
            </>
          ) : (
            <>
              <Tabs
                items={[
                  { key: 'work', label: t('aiEmployeeProfiles.tabWork'), children: tabWork },
                  { key: 'personal', label: t('aiEmployeeProfiles.tabPersonal'), children: tabPersonal },
                  { key: 'memory', label: t('aiEmployeeProfiles.tabMemory'), children: tabMemory }
                ]}
              />
              <Button type="primary" onClick={submit} style={{ marginTop: 16 }}>
                {t('aiEmployeeProfiles.save')}
              </Button>
            </>
          )}
        </Form>
      </Drawer>

      <Modal
        title={t('aiEmployeeProfiles.aiModalTitle')}
        open={aiModalOpen}
        onOk={runAiExpand}
        onCancel={() => setAiModalOpen(false)}
        confirmLoading={aiSubmitting}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          {getAiExpandFieldLabel(aiFieldKey)}
        </Text>
        <TextArea rows={3} value={aiHint} onChange={(e) => setAiHint(e.target.value)} placeholder={t('aiEmployeeProfiles.aiHintPlaceholder')} />
      </Modal>
    </div>
  );
}

export default AiEmployeeProfilesPage;
